import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Bell/alert sounds ke liye hook.
 *
 * Kya theek karta hai:
 *  - Autoplay policy: browser/WebView bina user gesture ke play() block karta hai.
 *    Pehle touch/click/keypress par saare sounds "unlock" (muted play -> pause) ho jate hain.
 *  - Already playing audio par dobara play() call karne se awaaz nahi aati thi ->
 *    ab har baar pause + currentTime = 0 karke bajata hai.
 *  - unlocked = false milta hai to UI me "Sound चालू करें" button dikha sakte ho
 *    (Display screen ke liye zaroori, wahan koi tap hi nahi karta).
 *
 * Use:
 *   const { ring, unlocked, unlock } = useBell({
 *     bell: '/assets/audio/bell.wav',
 *     staff: '/assets/audio/staff.wav',
 *   })
 *   ring('bell')
 */
export default function useBell(sources) {
  const sourcesRef = useRef(sources)
  const audiosRef = useRef({})
  const unlockedRef = useRef(false)
  const [unlocked, setUnlocked] = useState(false)

  // audio elements ek hi baar banao
  useEffect(() => {
    const audios = {}
    Object.keys(sourcesRef.current || {}).forEach((key) => {
      const a = new Audio(sourcesRef.current[key])
      a.preload = 'auto'
      a.playsInline = true
      try { a.load() } catch (e) { /* ignore */ }
      audios[key] = a
    })
    audiosRef.current = audios

    return () => {
      Object.keys(audios).forEach((k) => {
        try { audios[k].pause() } catch (e) { /* ignore */ }
      })
      audiosRef.current = {}
    }
  }, [])

  const unlock = useCallback(async () => {
    if (unlockedRef.current) return true
    const list = Object.keys(audiosRef.current).map((k) => audiosRef.current[k])
    if (!list.length) return false

    const results = await Promise.all(list.map(async (a) => {
      try {
        a.muted = true
        await a.play()
        a.pause()
        a.currentTime = 0
        return true
      } catch (e) {
        return false
      } finally {
        a.muted = false
      }
    }))

    const ok = results.every(Boolean)
    unlockedRef.current = ok
    setUnlocked(ok)
    return ok
  }, [])

  // pehle user gesture par unlock (jab tak safal na ho, sunte raho)
  useEffect(() => {
    const handler = () => { unlock() }
    const opts = { passive: true }
    window.addEventListener('pointerdown', handler, opts)
    window.addEventListener('touchstart', handler, opts)
    window.addEventListener('keydown', handler)
    return () => {
      window.removeEventListener('pointerdown', handler)
      window.removeEventListener('touchstart', handler)
      window.removeEventListener('keydown', handler)
    }
  }, [unlock])

  const ring = useCallback((key = 'bell') => {
    const a = audiosRef.current[key]
    if (!a) {
      console.warn('[bell] unknown sound:', key)
      return
    }
    try {
      a.pause()
      a.currentTime = 0
    } catch (e) { /* ignore */ }

    const p = a.play()
    if (p && p.catch) {
      p.catch((err) => {
        // sabse common: NotAllowedError = autoplay blocked
        console.warn('[bell] playback blocked:', key, err && err.name)
        unlockedRef.current = false
        setUnlocked(false)
      })
    }
  }, [])

  return { ring, unlock, unlocked }
}